import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, Stack } from '@mantine/core';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Form, SwitchControl, identitySchema } from '@/core/components';

import { useCurrentUser } from '@feature/security';
import { Id, JobTitleSelectControl, jobTitleList } from '@feature/utils';

import { GovDomainSelectControl } from './controls/GovDomainSelectControl';

interface GovEvaluationUserDataModalProps {
  onSubmit: (v: DataSchema) => void;
  isSubmitting: boolean;
  onClose: () => void;

  evaluationId: Id;
}

const schema = z.object({
  canLeadDoctorate: z.boolean(),
  jobTitle: z.enum(jobTitleList),
  domain: identitySchema,
});

type DataSchema = z.infer<typeof schema>;

export function GovEvaluationUserDataModalForm(props: GovEvaluationUserDataModalProps) {
  const { onClose, isSubmitting, onSubmit, evaluationId } = props;

  const { jobTitle } = useCurrentUser();
  const defaultValues = useMemo(
    () => ({
      canLeadDoctorate: false,
      jobTitle,
    }),
    [jobTitle],
  );

  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<DataSchema>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  return (
    <Modal
      opened
      centered
      onClose={onClose}
      title={t('govEvaluation.userDataForm.title')}
    >
      <Form
        onSubmit={handleSubmit(onSubmit)}
        onCancel={onClose}
        title=""
        isSubmitting={isSubmitting}
      >
        <Stack gap="xl">
          <GovDomainSelectControl
            label={t('govEvaluation.studyDomain')}
            name="domain"
            control={control}
            isSubmitting={isSubmitting}
            evaluationId={evaluationId}
          />
          <JobTitleSelectControl
            label={t('govEvaluation.jobTitle')}
            name="jobTitle"
            control={control}
            isSubmitting={isSubmitting}
            allowDeselect={false}
          />
          <SwitchControl
            label={t('govEvaluation.canLeadDoctorate')}
            name="canLeadDoctorate"
            control={control}
            isSubmitting={isSubmitting}
          />
        </Stack>
      </Form>
    </Modal>
  );
}
